var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/EventBus/EventBusProvider.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var EventBusContext = createContext(false);
var EventBusProvider = ({
  children
}) => {
  return (
    // eslint-disable-next-line react/jsx-boolean-value
    /* @__PURE__ */ jsx(EventBusContext.Provider, { value: true, children })
  );
};
var useEventBusEnabled = () => {
  try {
    return useContext(EventBusContext);
  } catch {
    return false;
  }
};

// src/channels/dialog/DialogManager.tsx
import React3, { useMemo } from "react";
import { getPortalRoot as getPortalRoot2 } from "ext/lib/document";
import StyleProvider from "ext/components/StyleProvider";
import { createPortal } from "react-dom";

// src/channels/dialog/hooks/use-dialog-manager.ts
import { useEffect, useState, useRef } from "react";

// src/EventBus/EventBus.ts
var EventBus = class {
  constructor() {
    __publicField(this, "channels", /* @__PURE__ */ new Map());
  }
  ensureChannel(channel) {
    if (!this.channels.has(channel)) {
      this.channels.set(channel, /* @__PURE__ */ new Map());
    }
    return this.channels.get(channel);
  }
  on(channel, event, handler) {
    const channelEvents = this.ensureChannel(channel);
    if (!channelEvents.has(event)) {
      channelEvents.set(event, []);
    }
    const handlers = channelEvents.get(event);
    if (handlers) {
      handlers.push(handler);
    }
    return () => this.off(channel, event, handler);
  }
  emit(channel, event, data) {
    const channelEvents = this.channels.get(channel);
    if (!channelEvents) return;
    const handlers = channelEvents.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(
            `Error in event handler for "${channel}:${event}":`,
            error
          );
        }
      });
    }
  }
  once(channel, event, handler) {
    const onceWrapper = (data) => {
      handler(data);
      this.off(channel, event, onceWrapper);
    };
    return this.on(channel, event, onceWrapper);
  }
  off(channel, event, handler) {
    const channelEvents = this.channels.get(channel);
    if (!channelEvents) return;
    if (!handler) {
      channelEvents.delete(event);
      return;
    }
    const handlers = channelEvents.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
      if (handlers.length === 0) {
        channelEvents.delete(event);
      }
    }
  }
  deleteChannel(channel) {
    this.channels.delete(channel);
  }
  clear() {
    this.channels.clear();
  }
  // Debug utilities
  getActiveChannels() {
    return [...this.channels.keys()];
  }
  getChannelEvents(channel) {
    const channelEvents = this.channels.get(channel);
    return channelEvents ? [...channelEvents.keys()] : [];
  }
  getHandlerCount(channel, event) {
    return this.channels.get(channel)?.get(event)?.length || 0;
  }
};
var eventBus = new EventBus();

// src/channels/CreateChannelBus.ts
var createChannelBus = (channel) => ({
  on: (event, handler) => eventBus.on(channel, event, handler),
  once: (event, handler) => eventBus.once(channel, event, handler),
  emit: (event, data) => eventBus.emit(channel, event, data),
  off: (event, handler) => eventBus.off(channel, event, handler)
});

// src/constants/channels.ts
var CHANNELS = {
  DIALOG: "dialog"
  // Future channels can be added here, add a new
  // value to the Channels
  // ANALYTICS: 'analytics',
  // NAVIGATION: 'navigation',
};

// src/channels/dialog/dialog.ts
var dialogBus = createChannelBus(CHANNELS.DIALOG);
var DialogEventEmitter = class {
  constructor() {
    __publicField(this, "open", (data) => {
      dialogBus.emit("open", data);
    });
    __publicField(this, "close", (dialogId) => {
      dialogBus.emit("close", { dialogId });
    });
    __publicField(this, "closeAll", () => {
      dialogBus.emit("close:all");
    });
    __publicField(this, "closeChildren", (parentDialogId) => {
      dialogBus.emit("close:children", { parentDialog: parentDialogId });
    });
    __publicField(this, "openChild", (data) => {
      dialogBus.emit("open:child", data);
    });
    __publicField(this, "notifyStateChange", (data) => {
      dialogBus.emit("stateChanged", data);
    });
  }
};
var dialogEvents = new DialogEventEmitter();
var parseDialogId = (dialogId) => {
  const [component, ...id] = dialogId.split("-");
  return {
    dialogBlock: component,
    // e.g., "Button"
    nodeId: id.join("-")
    // e.g., "fere34-adfer"
  };
};
var createDialogId = (dialogBlock, nodeId) => {
  return `${dialogBlock}-${nodeId}`;
};

// src/channels/dialog/hooks/use-dialog-manager.ts
var useDialogManager = () => {
  const prevDialogRef = useRef(null);
  const [state, setState] = useState({
    activeDialog: null,
    childDialogs: /* @__PURE__ */ new Map(),
    isOpen: false
  });
  const handleOpenDialog = (data) => {
    const isSameDialog = prevDialogRef.current === data.dialogId;
    if (prevDialogRef.current && !isSameDialog) {
      dialogBus.emit("close", { dialogId: prevDialogRef.current });
    }
    prevDialogRef.current = data.dialogId;
    setState((prevState) => {
      if (isSameDialog) {
        return {
          ...prevState,
          activeDialog: data,
          isOpen: true,
          childDialogs: prevState.childDialogs
          // Explicitly preserve existing children
        };
      }
      const preservedChildren = new Map(
        [...prevState.childDialogs].filter(
          ([, child]) => child.parentDialogId === data.dialogId
        )
      );
      return {
        ...prevState,
        activeDialog: data,
        isOpen: true,
        childDialogs: preservedChildren
      };
    });
  };
  const handleCloseDialog = ({ dialogId }) => {
    setState((prevState) => {
      if (prevState.childDialogs.has(dialogId)) {
        const newChildDialogs = new Map(prevState.childDialogs);
        newChildDialogs.delete(dialogId);
        return {
          ...prevState,
          childDialogs: newChildDialogs
        };
      }
      if (dialogId && prevState.activeDialog?.dialogId !== dialogId) {
        return prevState;
      }
      return {
        ...prevState,
        activeDialog: null,
        isOpen: false,
        childDialogs: /* @__PURE__ */ new Map()
      };
    });
  };
  const handleCloseAllDialogs = () => {
    setState({
      activeDialog: null,
      childDialogs: /* @__PURE__ */ new Map(),
      isOpen: false
    });
  };
  const handleOpenChild = (data) => {
    setState((prevState) => {
      if (prevState.childDialogs.has(data.dialogId)) {
        return prevState;
      }
      const newChildDialogs = /* @__PURE__ */ new Map();
      newChildDialogs.set(data.dialogId, data);
      return {
        ...prevState,
        childDialogs: newChildDialogs
      };
    });
  };
  useEffect(() => {
    const unsubscribeOpen = dialogBus.on("open", handleOpenDialog);
    const unsubscribeClose = dialogBus.on("close", handleCloseDialog);
    const unsubscribeCloseAll = dialogBus.on(
      "close:all",
      handleCloseAllDialogs
    );
    const unsubscribeOpenChild = dialogBus.on("open:child", handleOpenChild);
    return () => {
      unsubscribeOpen();
      unsubscribeClose();
      unsubscribeCloseAll();
      unsubscribeOpenChild();
    };
  }, []);
  const open = (data) => {
    dialogBus.emit("open", data);
  };
  const close = (dialogId) => {
    dialogBus.emit("close", { dialogId });
  };
  const closeAll = () => {
    dialogBus.emit("close:all");
  };
  const closeChildren = (parentDialogId) => {
    dialogBus.emit("close:children", { parentDialog: parentDialogId });
  };
  const openChild = (data) => {
    dialogBus.emit("open:child", data);
  };
  return {
    ...state,
    open,
    close,
    closeAll,
    closeChildren,
    openChild
  };
};

// src/channels/dialog/DialogView.tsx
import { useRef as useRef2, useEffect as useEffect2, useCallback, useContext as useContext2 } from "react";
import { getPortalRoot } from "ext/lib/document";
import { SafeAreaContext } from "ext/components/SafeArea";

// src/channels/dialog/DialogRegistry.ts
var DialogRegistry = class {
  constructor() {
    __publicField(this, "registry", /* @__PURE__ */ new Map());
  }
  /**
   * Register a dialog component for a specific block type
   */
  register(registration) {
    this.registry.set(registration.blockType, registration);
  }
  /**
   * Get the dialog component for a block type
   */
  getDialogComponent(blockType) {
    return this.registry.get(blockType);
  }
  /**
   * Get all registered dialog components
   */
  getAllRegistrations() {
    return [...this.registry.values()];
  }
  /**
   * Check if a block type has a registered dialog
   */
  hasDialog(blockType) {
    return this.registry.has(blockType);
  }
  /**
   * Get all registered block types
   */
  getRegisteredBlockTypes() {
    return [...this.registry.keys()];
  }
  /**
   * Clear all registrations (mainly for testing)
   */
  clear() {
    this.registry.clear();
  }
};
var dialogRegistry = new DialogRegistry();

// src/channels/dialog/DialogView.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var DialogWithClickOutside = ({ dialog, DialogComponent }) => {
  const containerRef = useRef2(null);
  const { contextWindow } = useContext2(SafeAreaContext);
  const { onClose, ...restProps } = dialog.props || {};
  const handleClose = useCallback(
    (event) => {
      onClose?.(event);
    },
    [onClose]
  );
  const isChildDialog = "parentDialogId" in dialog;
  useEffect2(() => {
    if (isChildDialog) {
      return;
    }
    const portalRoot = getPortalRoot();
    const handler = (event) => {
      const path = event.composedPath();
      const [target] = path;
      const dialogElement = dialog.elementRef?.current || containerRef.current;
      if (!dialogElement || !(target instanceof Node)) {
        return;
      }
      const inside = path.includes(dialogElement);
      const insidePortal = portalRoot && path.includes(portalRoot);
      if (!inside && !insidePortal) {
        handleClose?.(event);
      }
    };
    document.addEventListener("click", handler, true);
    const iframeDoc = contextWindow?.document;
    const isInIframe = iframeDoc && iframeDoc !== document;
    if (isInIframe) {
      iframeDoc.addEventListener("click", handler, true);
    }
    return () => {
      document.removeEventListener("click", handler, true);
      if (isInIframe) {
        iframeDoc.removeEventListener("click", handler, true);
      }
    };
  }, [handleClose, isChildDialog, dialog.elementRef, contextWindow]);
  const dialogContent = restProps?.nodeId ? /* @__PURE__ */ jsx2(
    DialogComponent,
    {
      ...restProps,
      ref: dialog.elementRef,
      nodeId: restProps.nodeId,
      onClose: handleClose,
      elementRef: dialog.elementRef,
      type: "node"
    }
  ) : /* @__PURE__ */ jsx2(
    DialogComponent,
    {
      ...restProps,
      ref: dialog.elementRef,
      block: restProps?.block,
      onClose: handleClose,
      elementRef: dialog.elementRef,
      type: "block"
    }
  );
  return /* @__PURE__ */ jsx2("div", { ref: containerRef, style: { display: "contents" }, children: dialogContent });
};
var DialogView = ({ dialog }) => {
  const parsedId = parseDialogId(dialog.dialogId);
  if (!parsedId) {
    console.warn(`[DialogView] Invalid dialog ID format: ${dialog.dialogId}`);
    return null;
  }
  const registration = dialogRegistry.getDialogComponent(parsedId.dialogBlock);
  if (!registration) {
    console.warn(
      `[DialogView] No registered component for block type: ${parsedId.dialogBlock}, ${dialog.dialogId}`
    );
    return null;
  }
  const DialogComponent = registration.dialogComponent;
  return /* @__PURE__ */ jsx2(DialogWithClickOutside, { dialog, DialogComponent });
};

// src/channels/dialog/DialogManager.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var getDialogRoot = getPortalRoot2;
var MemoizedStyleProvider = React3.memo(({ target, pattern, children }) => /* @__PURE__ */ jsx3(StyleProvider, { target, pattern, global: true, children }));
var EventBusDialogManager = () => {
  const { activeDialog, childDialogs, isOpen } = useDialogManager();
  const portalRoot = getDialogRoot();
  const dialogs = useMemo(() => {
    if (!isOpen) return [];
    const mainDialog = activeDialog ? /* @__PURE__ */ jsx3(DialogView, { dialog: activeDialog }, activeDialog.dialogId) : null;
    const nestedDialogElements = [...childDialogs.values()].map((dialog) => /* @__PURE__ */ jsx3(DialogView, { dialog }, dialog.dialogId));
    return [mainDialog, ...nestedDialogElements].filter(Boolean);
  }, [activeDialog, childDialogs, isOpen]);
  if (!isOpen || dialogs.length === 0) {
    return null;
  }
  const content = /* @__PURE__ */ jsx3(MemoizedStyleProvider, { target: portalRoot, pattern: "flow", children: dialogs });
  return portalRoot ? createPortal(content, portalRoot) : content;
};

// src/channels/dialog/hooks/use-dialog-api.ts
import { useCallback as useCallback2, useEffect as useEffect3 } from "react";
var useDialogAPI = ({
  dialogId,
  onClose
}) => {
  useEffect3(() => {
    if (!dialogId || !onClose) return;
    const unsubscribe = dialogBus.on("close", ({ dialogId: closedId }) => {
      if (dialogId === closedId) {
        onClose();
      }
    });
    return unsubscribe;
  }, [dialogId, onClose]);
  const open = useCallback2((data) => {
    dialogBus.emit("open", data);
  }, []);
  const close = useCallback2((id) => {
    dialogBus.emit("close", { dialogId: id });
  }, []);
  const closeAll = useCallback2(() => {
    dialogBus.emit("close:all");
  }, []);
  const closeChildren = useCallback2((parentDialogId) => {
    dialogBus.emit("close:children", { parentDialog: parentDialogId });
  }, []);
  const openChild = useCallback2((data) => {
    dialogBus.emit("open:child", data);
  }, []);
  return {
    open,
    close,
    closeAll,
    closeChildren,
    openChild
  };
};

// src/EventBus/use-event-bus.ts
import { useEffect as useEffect4, useRef as useRef3 } from "react";
var useEventBus = (channel, event, handler, deps = []) => {
  const handlerRef = useRef3(handler);
  handlerRef.current = handler;
  useEffect4(() => {
    const wrappedHandler = (data) => handlerRef.current(data);
    const unsubscribe = eventBus.on(channel, event, wrappedHandler);
    return unsubscribe;
  }, [channel, event, ...deps]);
};
var createChannelHook = (channelName) => {
  return (event, handler, deps = []) => useEventBus(channelName, event, handler, deps);
};

// src/channels/dialog/hooks/use-dialog-events.ts
var useDialogEvents = createChannelHook("dialog");

// src/channels/dialog/RegisteredDialog.tsx
import { useEffect as useEffect5, useMemo as useMemo2 } from "react";
var RegisteredDialog = ({
  blockType,
  component
}) => {
  const registration = useMemo2(() => {
    if (dialogRegistry.hasDialog(blockType)) {
      return null;
    }
    return {
      blockType,
      dialogComponent: component
    };
  }, [blockType, component]);
  useEffect5(() => {
    if (registration) {
      dialogRegistry.register(registration);
    }
  }, [registration]);
  return null;
};
export {
  EventBusDialogManager as DialogManager,
  EventBusProvider,
  RegisteredDialog,
  createDialogId,
  dialogRegistry,
  useDialogAPI,
  useDialogEvents,
  useEventBusEnabled
};
//# sourceMappingURL=index.mjs.map